import { Pause, Play } from 'lucide-react';
import { useMemo, useState } from 'react';

import { AcidGas3D } from './AcidGas3D';
import {
  ACID_GAS_CHALLENGES,
  ACID_GAS_FIELDS,
  ACID_GAS_QUESTION,
  DEFAULT_ACID_GAS_SETUP,
  DEFAULT_GAS_REACTION_ID,
  GAS_REACTIONS,
  GAS_SYRINGE_CAPACITY_ML,
  SLC_MOLAR_VOLUME_L,
  type AcidGasSetup,
  acidGasSceneState,
  calculateAcidGas,
  gasReactionById,
  gasReactionLabel,
  sig3,
} from './acidGasExplore';
import { balancedEquation } from './acidReactions';
import { ExploreChallenges } from './ExploreChallenges';
import { ModelInputPanel, WorkedStepsPanel } from './ModelInputs';
import { formatSpecies } from './reactionPredictionData';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';

const PRESETS: Array<{ id: string; label: string; setup: Partial<AcidGasSetup> }> = [
  { id: 'start', label: 'Just added', setup: { progressPct: 0 } },
  { id: 'half', label: 'Halfway', setup: { progressPct: 50 } },
  { id: 'done', label: 'Finished', setup: { progressPct: 100 } },
  { id: 'acid-limits', label: 'Acid runs out', setup: { massG: 1, concentrationMolL: 0.5, volumeMl: 10, progressPct: 100 } },
];

/**
 * Explore: "How much gas?" (CT-INTERACT). One setup drives the 3D apparatus, the readouts and the
 * worked calculation. Invalid input keeps the last valid setup.
 */
export function AcidGasExplorer() {
  const [reactionId, setReactionId] = useState(DEFAULT_GAS_REACTION_ID);
  const [setup, setSetup] = useState<AcidGasSetup>(DEFAULT_ACID_GAS_SETUP);
  const reducedMotion = usePrefersReducedMotion();
  const [playing, setPlaying] = useState<boolean | null>(null);
  const moving = playing ?? !reducedMotion;
  const reaction = gasReactionById(reactionId);
  const result = useMemo(() => calculateAcidGas(reaction, setup), [reaction, setup]);
  const sceneState = useMemo(() => acidGasSceneState(result, setup), [result, setup]);
  const gasLabel = formatSpecies(result.gasFormula);
  const limitingText =
    result.limiting === 'both' ? 'Both run out together' : result.limiting === 'solid' ? `${result.partner.name} runs out first` : 'The acid runs out first';
  const syringeShare = Math.min(result.gasMl / GAS_SYRINGE_CAPACITY_ML, 1);

  return (
    <section
      className="my-6 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8"
      aria-labelledby="acid-gas-heading"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">Explore</p>
          <h2 id="acid-gas-heading" className="mt-2 text-2xl font-extrabold text-ink">
            How much gas?
          </h2>
          <p className="mt-1 max-w-3xl text-sm font-medium text-slate2">
            Choose a reaction that makes a gas, then set the mass of solid and the acid. The model finds the limiting reagent
            and the gas volume at 25 °C and 100 kPa ({SLC_MOLAR_VOLUME_L} L/mol), as collected in a gas syringe.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setPlaying(!moving)}
          className="inline-flex items-center gap-2 rounded-full border border-hairline px-4 py-2 text-sm font-extrabold text-ink"
        >
          {moving ? <Pause className="h-4 w-4" aria-hidden="true" /> : <Play className="h-4 w-4" aria-hidden="true" />}
          {moving ? 'Pause bubbles' : 'Play bubbles'}
        </button>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <div className="min-w-0">
          <label className="block text-sm font-extrabold text-ink">
            Reaction
            <select
              aria-label="Gas reaction"
              value={reactionId}
              onChange={(event) => setReactionId(event.target.value)}
              className="mt-2 w-full rounded-2xl border border-hairline bg-surface px-4 py-3 font-bold outline-none focus:border-brand-sky"
            >
              {GAS_REACTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {gasReactionLabel(option)}
                </option>
              ))}
            </select>
          </label>
          <p className="mt-2 break-words font-mono text-sm font-extrabold text-ink" data-testid="gas-equation">
            {balancedEquation(reaction)}
          </p>
          <div className="mt-4">
            <ModelInputPanel
              legend="Set up your flask"
              fields={ACID_GAS_FIELDS}
              values={setup}
              onChange={(next) => setSetup(next as AcidGasSetup)}
              sliderKey="progressPct"
            />
          </div>
          <div className="mt-3 flex flex-wrap gap-2" aria-label="Gas presets">
            {PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => setSetup({ ...setup, ...preset.setup })}
                className="rounded-full border border-hairline bg-surface px-3 py-1.5 text-xs font-extrabold text-ink hover:border-brand-sky/50"
              >
                {preset.label}
              </button>
            ))}
          </div>
          <div className="mt-4">
            <ExploreChallenges question={ACID_GAS_QUESTION} challenges={ACID_GAS_CHALLENGES} result={{ setup, result }} />
          </div>
        </div>

        <div className="min-w-0">
          <figure className="min-w-0">
            <figcaption className="mb-2 text-xs font-extrabold uppercase tracking-[0.12em] text-slate2">What you see</figcaption>
            <AcidGas3D
              state={sceneState}
              playing={moving}
              label={`Conical flask with ${sig3(setup.volumeMl)} mL of acid and ${result.partner.name}; gas syringe reading ${sig3(sceneState.syringeMl)} mL${result.syringeFull ? ' (full)' : ''}.`}
            />
          </figure>
          <div className="mt-3 grid gap-3 sm:grid-cols-3" role="group" aria-label="Gas readout">
            <div className="rounded-2xl border border-brand-sky/25 bg-wash-sky p-4">
              <p className="text-xs font-extrabold uppercase tracking-wide text-brand-sky">Gas collected</p>
              <p className="mt-1 text-xl font-extrabold text-ink" data-testid="gas-volume">
                {sig3(result.gasMl)} mL {gasLabel}
              </p>
              <div className="mt-2 h-3 overflow-hidden rounded-full bg-canvas-pure" aria-hidden="true">
                <div className="h-full rounded-full bg-brand-sky" style={{ width: `${syringeShare * 100}%` }} />
              </div>
              <p className="mt-1 text-xs font-bold text-slate2">
                Syringe 0–{GAS_SYRINGE_CAPACITY_ML} mL{result.syringeFull ? ' · full' : ''}
              </p>
            </div>
            <div className="rounded-2xl border border-brand-coral/25 bg-wash-coral p-4">
              <p className="text-xs font-extrabold uppercase tracking-wide text-brand-coral">Limiting reagent</p>
              <p className="mt-1 text-base font-extrabold text-ink" data-testid="gas-limiting">
                {limitingText.charAt(0).toUpperCase() + limitingText.slice(1)}
              </p>
            </div>
            <div className="rounded-2xl border border-hairline bg-surface p-4">
              <p className="text-xs font-extrabold uppercase tracking-wide text-slate2">Left in the flask</p>
              <p className="mt-1 text-sm font-extrabold text-ink">
                {sig3(result.solidLeftG)} g {result.partner.name} · {sig3(result.acidLeftMol)} mol acid
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-5">
        <WorkedStepsPanel
          title="How the model works it out"
          steps={result.steps}
          stepTestIdPrefix="gas-step"
          result={`Result: ${sig3(result.gasMl)} mL of ${result.gasName} (${sig3(result.gasMol)} mol)${
            setup.progressPct < 100 ? ` so far, of ${sig3(result.finalGasMl)} mL when finished` : ''
          }`}
          resultTestId="gas-result"
          limitNote={result.limitNote}
        />
      </div>

      <p className="mt-4 text-xs font-medium leading-5 text-slate2">
        Model boundaries: every gas particle is counted at 25 °C and 100 kPa. Carbon dioxide dissolves a little in water, so a
        real syringe reads slightly less; hydrogen hardly dissolves. Reaction progress is the share of the limiting reagent
        used, not a time. Above about 1 mol/L the acid reacts more vigorously, so take extra care in a real practical.
      </p>
    </section>
  );
}
